import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { Loader2 } from 'lucide-react';

const DeliveryOrderPrint = forwardRef(({ docketId, formData, companyData }, ref) => {
  const [company, setCompany] = useState(companyData || null);
  const [loading, setLoading] = useState(!companyData);

  const backendUrl = process.env.REACT_APP_BACKEND_URL;

  const getAuthHeaders = () => {
    const token = localStorage.getItem('auth_token');
    return {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    };
  };

  useEffect(() => {
    const fetchCompanyDetails = async () => {
      if (companyData) {
        setCompany(companyData);
        setLoading(false);
        return;
      }
      try {
        const docketResponse = await fetch(`${backendUrl}/api/dockets/${docketId}`, {
          headers: getAuthHeaders()
        });
        if (docketResponse.ok) {
          const docketData = await docketResponse.json();
          const companyResponse = await fetch(`${backendUrl}/api/companies/${docketData.company_id}`, {
            headers: getAuthHeaders()
          });
          if (companyResponse.ok) {
            setCompany(await companyResponse.json());
          }
        }
      } catch (error) {
        console.error('Failed to fetch company details:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchCompanyDetails();
  }, [docketId, companyData, backendUrl]);

  useImperativeHandle(ref, () => ({
    print: () => window.print()
  }));

  const formatDate = (dateString) => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toLocaleDateString('en-IN', {
        day: '2-digit', month: '2-digit', year: 'numeric'
      });
    } catch {
      return dateString;
    }
  };

  const fd = (val, date) =>
    val && date ? `${val} / ${formatDate(date)}` : val || '';

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <Loader2 className="w-6 h-6 animate-spin" />
      </div>
    );
  }

  const companyName    = company?.name      || 'COMPANY NAME';
  // Convert Drive URL to thumbnail URL to avoid CORS/403 issues
  const getLogoUrl = (url) => {
    if (!url) return null;
    const fileId = url.match(/id=([^&]+)/)?.[1] || url.match(/\/d\/([^/]+)\//)?.[1];
    if (fileId) return `https://drive.google.com/thumbnail?id=${fileId}&sz=w1000`;
    return url;
  };
  const companyLogo    = getLogoUrl(company?.logo_url);
  const companyAddress = [company?.address, company?.city, company?.state, company?.pincode].filter(Boolean).join(', ');
  const companyPhone   = company?.phone     || '';
  const companyEmail   = company?.email     || '';
  const companyWebsite = company?.website   || '';
  const companyGst     = company?.gst_number || '';
  const companyCin     = company?.cin_number || '';

  const tableRows = [
    { label: 'D/O No / Date',            value: fd(formData.do_no, formData.do_date) },
    { label: 'IGM No / Date',            value: fd(formData.igm_no, formData.igm_date) },
    { label: 'Flight Name',              value: formData.airline || formData.flight_name || '' },
    { label: 'Flight No / Date',         value: fd(formData.flight_no, formData.flight_date) },
    { label: 'Airport of Load',          value: formData.airport_of_load || '' },
    { label: 'Airport of Discharge',     value: formData.airport_of_discharge || '' },
    { label: 'MAWB No / Date',           value: fd(formData.mawb_no, formData.mawb_date) },
    { label: 'No. of Packages (MAWB)',   value: formData.mawb_packages || '' },
    { label: 'Gross Weight (MAWB)',      value: formData.mawb_gross_weight ? `${formData.mawb_gross_weight} KG` : '' },
    { label: 'Description (MAWB)',       value: formData.mawb_description || '' },
    { label: 'HAWB No / Date',           value: fd(formData.hawb_no, formData.hawb_date) },
    { label: 'No. of Packages (HAWB)',   value: formData.hawb_packages || '' },
    { label: 'Gross Weight (HAWB)',      value: formData.hawb_gross_weight ? `${formData.hawb_gross_weight} KG` : '' },
    { label: 'Description (HAWB)',       value: formData.hawb_description || '' },
  ];

  // Resolve address blocks: 'to' is Custodian/Terminal Operator, 'deliver_to' is Consignee/Importer
  const isDuplicate = formData.to && formData.deliver_to && formData.to.trim().toLowerCase() === formData.deliver_to.trim().toLowerCase();
  const custodianAddress = isDuplicate
    ? 'THE MANAGER, CSC/GSEC\nAir Cargo Complex, Sardar Vallabhbhai Patel Intl. Airport, Ahmedabad - 380003'
    : (formData.to || 'The Manager / Custodian\nAir Cargo Complex');
  const deliverToAddress = formData.deliver_to || (isDuplicate ? formData.to : '');

  return (
    <>
      {/* Print Content — id is applied by DOPrint parent */}
      <div
        className="bg-white print:bg-white"
        style={{ fontFamily: 'Arial, sans-serif', fontSize: '11px', lineHeight: '1.3' }}
      >
        <div className="p-6 print:p-6 relative">

          {/* Watermark */}
          <div
            className="absolute inset-0 flex items-center justify-center pointer-events-none overflow-hidden"
            style={{ zIndex: 0 }}
          >
            <div
              className="text-gray-200 font-bold text-center whitespace-nowrap transform -rotate-45"
              style={{ fontSize: '60px', opacity: 0.07, letterSpacing: '10px' }}
            >
              {companyName}
            </div>
          </div>

          {/* Content */}
          <div className="relative" style={{ zIndex: 1 }}>

            {/* ── Header ── */}
            <div className="flex items-start justify-between mb-2">
              {/* Logo */}
              <div className="flex-shrink-0">
                {companyLogo ? (
                  <img src={companyLogo} alt={companyName} className="h-16 w-auto object-contain" />
                ) : (
                  <div className="h-16 w-20 bg-gray-200 flex items-center justify-center text-xs text-gray-500 border">
                    LOGO
                  </div>
                )}
              </div>
              {/* Services */}
              <div className="text-right text-xs">
                <p className="font-semibold">Freight Forwarder</p>
                <p className="font-semibold">Custom Broker</p>
                <p className="font-semibold">Reefer Transportation</p>
                <p className="font-semibold">Logistics Solution</p>
                {companyWebsite && <p className="mt-1 text-blue-600">{companyWebsite}</p>}
              </div>
            </div>

            {/* Divider */}
            <div className="border-t-2 border-black mb-3"></div>

            {/* Title */}
            <div className="text-center mb-3">
              <h1 className="text-lg font-bold underline">DELIVERY ORDER</h1>
            </div>

            {/* Job No / Date */}
            <div className="flex justify-between mb-4 text-xs">
              <div><span className="font-semibold">Job No: </span>{formData.job_no || ''}</div>
              <div><span className="font-semibold">Date: </span>{formatDate(formData.date)}</div>
            </div>

            {/* ── Address Section ── */}
            <div className="mb-3 text-xs">
              <p className="whitespace-pre-wrap">{custodianAddress}</p>
            </div>

            <div className="mb-3 text-xs">
              <p className="font-semibold mb-1">Please deliver to:</p>
              <p className="whitespace-pre-wrap text-[10px] ml-2">{deliverToAddress}</p>
            </div>

            <div className="mb-4 text-xs italic">
              <p>Please deliver to / order the following package on collection of your charges.</p>
            </div>

            {/* ── Data Table ── */}
            <table className="w-full border-collapse border border-black text-xs mb-4">
              <tbody>
                {tableRows.map((row) => (
                  <tr key={row.label}>
                    <td
                      className="border border-black p-1.5 font-semibold bg-gray-50"
                      style={{ width: '45%', whiteSpace: 'nowrap' }}
                    >
                      {row.label}
                    </td>
                    <td className="border border-black p-1.5">
                      {row.value}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            {/* ── Signature ── */}
            <div className="mb-4">
              <div className="flex justify-end mb-4">
                <p className="font-semibold text-xs">For, {companyName}</p>
              </div>
              <div className="text-center mb-4">
                <p className="font-bold text-xs mb-1">ORIGINAL</p>
                <p className="text-[10px]">{companyName}</p>
              </div>
              <div className="flex justify-end">
                <div className="border-t border-black pt-1 w-44 text-xs">
                  <p className="text-center">Authorized Signatory</p>
                </div>
              </div>
            </div>

            {/* ── Footer ── */}
            <div id="do-footer" className="mt-4">
              <div className="border-t-2 border-black mb-2"></div>
              <div className="text-center" style={{ fontSize: '9px', lineHeight: '1.6' }}>
                <p style={{ fontWeight: 'bold', letterSpacing: '0.05em' }}>{companyName}</p>
                {companyAddress && (
                  <p>
                    <span style={{ fontWeight: 'bold' }}>Regd. Office: </span>
                    <span>{companyAddress}</span>
                  </p>
                )}
                {(companyPhone || companyEmail) && (
                  <p>
                    {companyPhone && (
                      <>
                        <span style={{ fontWeight: 'bold' }}>Phone: </span>
                        <span>{companyPhone}</span>
                      </>
                    )}
                    {companyPhone && companyEmail && <span> | </span>}
                    {companyEmail && (
                      <>
                        <span style={{ fontWeight: 'bold' }}>Email: </span>
                        <span>{companyEmail}</span>
                      </>
                    )}
                  </p>
                )}
                {(companyGst || companyCin) && (
                  <p>
                    {companyGst && (
                      <>
                        <span style={{ fontWeight: 'bold' }}>GSTIN: </span>
                        <span>{companyGst}</span>
                      </>
                    )}
                    {companyGst && companyCin && <span> | </span>}
                    {companyCin && (
                      <>
                        <span style={{ fontWeight: 'bold' }}>CIN/LLP: </span>
                        <span>{companyCin}</span>
                      </>
                    )}
                  </p>
                )}
              </div>
            </div>

          </div>
        </div>
      </div>

      {/* Print Styles */}
      <style>{`
        @media print {
          @page {
            size: A4;
            margin: 10mm;
          }

          html, body {
            margin: 0 !important;
            padding: 0 !important;
            background: white !important;
            height: auto !important;
            min-height: 0 !important;
          }

          body * {
            visibility: hidden;
          }

          #do-print-content, #do-print-content * {
            visibility: visible;
          }

          #do-print-content {
            position: absolute;
            left: 0;
            top: 0;
            width: 100%;
            min-height: calc(297mm - 20mm);
            display: flex !important;
            flex-direction: column !important;
            background: white !important;
            -webkit-print-color-adjust: exact !important;
            print-color-adjust: exact !important;
          }

          #do-print-content > div {
            flex: 1 !important;
            display: flex !important;
            flex-direction: column !important;
          }

          #do-print-content > div > div.relative {
            flex: 1 !important;
            display: flex !important;
            flex-direction: column !important;
          }

          #do-footer {
            margin-top: auto !important;
          }

          table, td, th {
            border-color: black !important;
          }

          .bg-gray-50 {
            background-color: #f9fafb !important;
            -webkit-print-color-adjust: exact !important;
          }
        }

        @media screen {
          #do-print-content {
            max-width: 210mm;
            margin: 0 auto;
            box-shadow: 0 0 10px rgba(0,0,0,0.1);
          }
        }
      `}</style>
    </>
  );
});

DeliveryOrderPrint.displayName = 'DeliveryOrderPrint';

export default DeliveryOrderPrint;
